// Tap-state rule module. Pure. No React, no Three.js. Owns the
// question "can this card transition to / from the tapped state right
// now?" so every tap-emitting subsystem (F hotkey, action menu,
// auto-tap on move, activated-ability tap-cost, card effects, remote
// network sync) shares one rule surface.
//
// Imperative side-effects (animation, physics body rotation, network
// broadcast, sound, log) live in GameBoard._applyTap, which calls
// these predicates before flipping card.tapped. Keeping the predicates
// pure keeps them unit-testable without mounting the renderer.
//
// ── Rules baked in ──
//
//   canTap. False if the card is currently disabled. Disabled
//                units lose all abilities and may not be tapped fresh
//                (codex Disabled).
//   canUntap. True universally for now. Reserved for future
//                statuses that lock untap (e.g. Stun-like effects).
//   nextTapToggle. Convenience for "toggle" callers (hotkey, action
//                menu): returns the next tap value, or null if the
//                rule refuses the transition.

import { isDisabled } from '../automation/scope';

/** May `card` be tapped (transition from untapped to tapped)? */
export function canTap(card) {
  if (!card) return false;
  // A disabled card with no abilities can still have its body rotated
  // by the renderer (sandbox legacy). But the engine refuses fresh
  // tap actions on it. Untap of an already-tapped disabled card stays
  // allowed so the player can recover after the status clears.
  if (isDisabled(card)) return false;
  return true;
}

/** May `card` be untapped (transition from tapped to untapped)? */
export function canUntap(card) {
  if (!card) return false;
  return true;
}

/**
 * For toggle-style callers: given the card's current state, return
 * the next tap value, or null if the engine refuses the transition.
 */
export function nextTapToggle(card) {
  if (!card) return null;
  if (card.tapped) return canUntap(card) ? false : null;
  return canTap(card) ? true : null;
}
